import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useStatus } from "../../popup/NotificationContext";
import {
  useAddLicenseMutation,
  useGetLicenseRecipientsQuery,
  useGetLicensesQuery,
} from "../../redux/logistics/logisticsApi";

const LICENSE_TYPES = ["Daily", "Monthly", "Yearly", "Lifetime", "Fuel", "Dashcam"];
const LICENSE_STATUSES = ["Completed", "Pending"];
const TRANSACTION_TYPES = ["Sell", "Reverse"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const formatDateCell = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const dateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const buildCsv = (rows) => {
  const headers = ["From", "To", "Currency", "Amount", "Units", "Date", "Status", "Payment Amount", "Payment Date", "Type", "Document", "Remarks", "Operation"];
  const body = rows.map((row) =>
    [
      row.fromName,
      row.toName,
      row.currency,
      row.unitAmount,
      row.count,
      formatDateCell(row.createdAt),
      row.status,
      row.paymentAmount,
      formatDateCell(row.paymentDate),
      row.type,
      row.documentPath,
      row.remarks,
      row.transactionType,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const renderSelectPlaceholder = (value, placeholder) => {
  if (value) return value;
  return <span style={{ color: "#9ca3af" }}>{placeholder}</span>;
};

const LicensePortalModal = ({ open = true, onClose }) => {
  const { showStatus } = useStatus();
  const { data: licensesPayload = [], refetch } = useGetLicensesQuery();
  const { data: recipients = [] } = useGetLicenseRecipientsQuery();
  const [addLicense, { isLoading: isSaving }] = useAddLicenseMutation();

  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedOperation, setSelectedOperation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("table");
  const [transactionToggle, setTransactionToggle] = useState("Sell");
  const [formData, setFormData] = useState({
    dealer: "",
    unitAmount: 100,
    currency: "INR",
    count: 0,
    tax: 18,
    total: 0,
    status: "Completed",
    paymentAmount: 0,
    paymentDate: dateInputValue(new Date()),
    type: "Yearly",
    remarks: "",
    transactionType: "Sell",
    file: null,
  });

  const normalizedRecipients = useMemo(() => (Array.isArray(recipients) ? recipients : []), [recipients]);
  const normalizedLicenses = useMemo(
    () =>
      (Array.isArray(licensesPayload) ? licensesPayload : []).map((license) => ({
        ...license,
        fromName: license?.createdBy?.username || license?.issuer?.username || "-",
        toName: license?.assignedTo?.username || license?.dealer || "-",
        currency: license?.currency || "INR",
        transactionType: license?.transactionType || "Sell",
        paymentAmount: Number(license?.paymentAmount ?? license?.total ?? 0),
        documentPath: license?.documentPath || "",
      })),
    [licensesPayload]
  );

  const filteredLicenses = useMemo(() => {
    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

    return normalizedLicenses.filter((license) => {
      if (selectedDealer && String(license?.assignedTo?._id || "") !== String(selectedDealer)) return false;
      if (selectedType && String(license?.type || "") !== selectedType) return false;
      if (selectedOperation && String(license?.transactionType || "") !== selectedOperation) return false;
      if (selectedStatus && String(license?.status || "") !== selectedStatus) return false;
      if (transactionToggle && String(license?.transactionType || "Sell") !== transactionToggle) return false;

      const createdAt = license?.createdAt ? new Date(license.createdAt) : null;
      if (fromDate && createdAt && createdAt < fromDate) return false;
      if (toDate && createdAt && createdAt > toDate) return false;
      return true;
    });
  }, [normalizedLicenses, selectedDealer, selectedType, selectedOperation, selectedStatus, dateFrom, dateTo, transactionToggle]);

  const totalPages = Math.max(1, Math.ceil(filteredLicenses.length / rowsPerPage));
  const pagedLicenses = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredLicenses.slice(start, start + rowsPerPage);
  }, [filteredLicenses, page, rowsPerPage]);

  const totalUnits = useMemo(
    () => filteredLicenses.reduce((sum, license) => sum + Number(license?.count || 0), 0),
    [filteredLicenses]
  );

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const baseTotal = Number(next.unitAmount || 0) * Number(next.count || 0);
      const taxAmount = (baseTotal * Number(next.tax || 0)) / 100;
      next.total = baseTotal + taxAmount;
      if (field !== "paymentAmount") {
        next.paymentAmount = next.total;
      }
      return next;
    });
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredLicenses);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "licenses.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Licenses CSV downloaded.");
  };

  const handleCreateLicense = async () => {
    if (!formData.dealer) {
      showStatus("warning", "Please select dealer.");
      return;
    }
    if (!formData.paymentDate) {
      showStatus("warning", "Please select payment date.");
      return;
    }
    if (Number(formData.count || 0) <= 0) {
      showStatus("warning", "Count must be greater than 0.");
      return;
    }

    const payload = new FormData();
    payload.append("dealer", formData.dealer);
    payload.append("unitAmount", String(formData.unitAmount || 0));
    payload.append("currency", String(formData.currency || "INR"));
    payload.append("count", String(formData.count || 0));
    payload.append("tax", String(formData.tax || 0));
    payload.append("total", String(formData.total || 0));
    payload.append("status", String(formData.status || "Completed"));
    payload.append("paymentAmount", String(formData.paymentAmount || 0));
    payload.append("paymentDate", String(formData.paymentDate || ""));
    payload.append("type", String(formData.type || "Yearly"));
    payload.append("remarks", String(formData.remarks || ""));
    payload.append("transactionType", String(formData.transactionType || "Sell"));
    if (formData.file) {
      payload.append("file", formData.file);
    }

    try {
      await addLicense(payload).unwrap();
      await refetch();
      setMode("table");
      setFormData({
        dealer: "",
        unitAmount: 100,
        currency: "INR",
        count: 0,
        tax: 18,
        total: 0,
        status: "Completed",
        paymentAmount: 0,
        paymentDate: dateInputValue(new Date()),
        type: "Yearly",
        remarks: "",
        transactionType: "Sell",
        file: null,
      });
      showStatus("success", "License purchased successfully.");
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Failed to save license.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{mode === "table" ? "Licenses" : "Purchase license"}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {mode === "table" ? (
          <>
            <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr 1fr 1fr 1fr 1.1fr auto auto" }, gap: 1.5, alignItems: "center" }}>
                <TextField
                  select
                  value={selectedDealer}
                  onChange={(e) => setSelectedDealer(e.target.value)}
                  sx={inputSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) {
                        return renderSelectPlaceholder("", "Select dealer");
                      }
                      const matched = normalizedRecipients.find((item) => String(item._id) === String(value));
                      return matched?.username || value;
                    },
                  }}
                >
                  <MenuItem value="">Select Dealer</MenuItem>
                  {normalizedRecipients.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      {item.username}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  sx={inputSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => renderSelectPlaceholder(value, "Select license type"),
                  }}
                >
                  <MenuItem value="">Select License Type</MenuItem>
                  {LICENSE_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={selectedOperation}
                  onChange={(e) => setSelectedOperation(e.target.value)}
                  sx={inputSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => renderSelectPlaceholder(value, "Select operation"),
                  }}
                >
                  <MenuItem value="">Select Operation</MenuItem>
                  {TRANSACTION_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={inputSx} InputProps={{ endAdornment: <InputAdornment position="end"><CalendarMonthOutlinedIcon fontSize="small" /></InputAdornment> }} />
                <TextField type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={inputSx} InputProps={{ endAdornment: <InputAdornment position="end"><CalendarMonthOutlinedIcon fontSize="small" /></InputAdornment> }} />
                <TextField
                  select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  sx={inputSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => renderSelectPlaceholder(value, "Select status"),
                  }}
                >
                  <MenuItem value="">Select status</MenuItem>
                  {LICENSE_STATUSES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <IconButton onClick={() => setPage(1)} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                  <SearchIcon />
                </IconButton>
                <IconButton onClick={() => setMode("create")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 88 }}>
                <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ fontWeight: 600, color: transactionToggle === "Sell" ? "#111827" : "#9ca3af" }}>Sell</Typography>
                <Switch checked={transactionToggle === "Reverse"} onChange={(e) => setTransactionToggle(e.target.checked ? "Reverse" : "Sell")} />
                <Typography sx={{ fontWeight: 600, color: transactionToggle === "Reverse" ? "#111827" : "#9ca3af" }}>Reverse</Typography>
                <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "56vh", borderRadius: 0 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["From", "To", "Currency", "Amount", "Units", "Date", "Status", "Payment Amount", "Payment Date", "Type", "Document", "Remarks", "Operation"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#4b5563", bgcolor: "#fff", borderBottom: "1px solid #d1d5db", whiteSpace: "nowrap" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedLicenses.length ? (
                    pagedLicenses.map((license) => (
                      <TableRow key={license._id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{license.fromName}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{license.toName}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{license.currency}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{Number(license.unitAmount || 0)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{Number(license.count || 0)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{formatDateCell(license.createdAt)}</TableCell>
                        <TableCell sx={{ fontWeight: 700, textTransform: "lowercase" }}>{String(license.status || "").toLowerCase()}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{Number(license.paymentAmount || 0)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{formatDateCell(license.paymentDate)}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{license.type}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {license.documentPath ? (
                            <a href={license.documentPath} target="_blank" rel="noreferrer">Click here</a>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{license.remarks || "-"}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{license.transactionType}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 6, color: "#6b7280" }}>
                        No licenses found.
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedLicenses.length ? (
                    <TableRow>
                      <TableCell colSpan={13} sx={{ fontWeight: 800, textAlign: "center", backgroundColor: "#fafafa" }}>
                        Total Units: {totalUnits}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {filteredLicenses.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredLicenses.length)} of {filteredLicenses.length} entries
              </Typography>
              <Pagination page={page} count={Math.max(1, totalPages)} onChange={(_, value) => setPage(value)} shape="rounded" />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
                <TextField
                  select
                  label="Dealer"
                  value={formData.dealer}
                  onChange={(e) => handleFormChange("dealer", e.target.value)}
                  sx={inputSx}
                >
                  <MenuItem value="">Select Dealer</MenuItem>
                  {normalizedRecipients.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      {item.username}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField label="Single Unit Amount" type="number" value={formData.unitAmount} onChange={(e) => handleFormChange("unitAmount", Number(e.target.value))} sx={inputSx} />
                <TextField label="Currency" value={formData.currency} InputProps={{ readOnly: true }} sx={inputSx} />
                <TextField label="Count" type="number" value={formData.count} onChange={(e) => handleFormChange("count", Number(e.target.value))} sx={inputSx} />
                <TextField label="Tax (%)" type="number" value={formData.tax} onChange={(e) => handleFormChange("tax", Number(e.target.value))} sx={inputSx} />
                <TextField label="Total" type="number" value={formData.total} InputProps={{ readOnly: true }} sx={inputSx} />
                <TextField select label="Status" value={formData.status} onChange={(e) => handleFormChange("status", e.target.value)} sx={inputSx}>
                  {LICENSE_STATUSES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Payment Amount" type="number" value={formData.paymentAmount} onChange={(e) => handleFormChange("paymentAmount", Number(e.target.value))} sx={inputSx} />
                <TextField type="date" label="Payment Date" value={formData.paymentDate} onChange={(e) => handleFormChange("paymentDate", e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
                <TextField select label="Type" value={formData.type} onChange={(e) => handleFormChange("type", e.target.value)} sx={inputSx}>
                  {LICENSE_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="file"
                  label="Document"
                  onChange={(e) => handleFormChange("file", e.target.files?.[0] || null)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx" }}
                  sx={inputSx}
                />
                <TextField select label="Transaction Type" value={formData.transactionType} onChange={(e) => handleFormChange("transactionType", e.target.value)} sx={inputSx}>
                  {TRANSACTION_TYPES.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Remarks"
                  multiline
                  minRows={5}
                  value={formData.remarks}
                  onChange={(e) => handleFormChange("remarks", e.target.value)}
                  sx={{ ...inputSx, gridColumn: { xs: "span 1", md: "span 2" } }}
                />
              </Box>
            </Box>
            <DialogActions sx={{ px: 3, pb: 3, justifyContent: "flex-start", gap: 1.5 }}>
              <Button variant="contained" onClick={handleCreateLicense} disabled={isSaving}>Save</Button>
              <Button variant="outlined" onClick={() => setMode("table")}>Cancel</Button>
            </DialogActions>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LicensePortalModal;
